/**
 * Phase 5 Component 24 follow-up — MA000027 Schedule J translation engine.
 *
 * On 1 January 2027, the transitional Dental Assistant + Pathology Collector
 * rate tables (cl 16.2(b) + 16.2(c) of MA000027) sunset. Workers grandfathered
 * under those classifications must be translated to the main Support Services
 * L1-L9 structure per Schedule J.
 *
 * J.3 floor rule: a translated worker's minimum rate is the HIGHER of:
 *   (a) the rate of their newly-translated Support Services classification, or
 *   (b) the rate they were paid on 31 March 2026 (the snapshot date).
 *
 * Workers must therefore have `legacy_classification` + `legacy_rate_at_31_mar_2026`
 * captured BEFORE 1 Jan 2027. After that, the snapshot is unrecoverable.
 *
 * Verified by aged-care-legislation agent (2026-04-29) against FWC consolidated
 * MA000027 PDF, Schedule J pp 106-110, inserted by PR795400 (corrected by
 * PR797960), ppc 1 April 2026.
 *
 * Sources:
 *   - https://www.fwc.gov.au/documents/modern_awards/pdf/ma000027.pdf (Sch J)
 *   - PR795400 + PR797960 determinations
 */

export type DentalAssistantLegacyLevel =
  | 'L1'
  | 'L2_UNQ_LT12MO'
  | 'L2_UNQ_12MO_TO_4YR'
  | 'L2_QUALIFIED'
  | 'L4'
  | 'L5';
export type PathologyCollectorLegacyLevel =
  | 'L4_UNQ_LT12MO'
  | 'L4_QUALIFIED'
  | 'L4_UNQ_GTE12MO_OR_QUAL_GTE12MO'
  | 'L5_UNQ_LT12MO_OR_QUAL_LT12MO'
  | 'L5_GTE12MO_EXP'
  | 'L6';

export type SupportServicesLevel = 'SS_L3' | 'SS_L5' | 'SS_L6' | 'SS_L7';

const DENTAL_ASSISTANT_TRANSLATION: Record<DentalAssistantLegacyLevel, SupportServicesLevel> = {
  L1: 'SS_L3',
  L2_UNQ_LT12MO: 'SS_L3',
  L2_UNQ_12MO_TO_4YR: 'SS_L5',
  L2_QUALIFIED: 'SS_L6',
  L4: 'SS_L6',
  L5: 'SS_L7',
};

const PATHOLOGY_COLLECTOR_TRANSLATION: Record<PathologyCollectorLegacyLevel, SupportServicesLevel> =
  {
    L4_UNQ_LT12MO: 'SS_L5',
    L4_QUALIFIED: 'SS_L6',
    L4_UNQ_GTE12MO_OR_QUAL_GTE12MO: 'SS_L7',
    L5_UNQ_LT12MO_OR_QUAL_LT12MO: 'SS_L6',
    L5_GTE12MO_EXP: 'SS_L7',
    L6: 'SS_L7',
  };

export interface TranslationInput {
  legacyRole: 'DENTAL_ASSISTANT' | 'PATHOLOGY_COLLECTOR';
  legacyLevel: DentalAssistantLegacyLevel | PathologyCollectorLegacyLevel;
  /**
   * The hourly rate the worker was paid on 31 March 2026 (snapshot date).
   * REQUIRED for J.3 floor calculation.
   */
  hourlyRateAt31Mar2026: number;
  /**
   * The hourly rate of the translated Support Services level on or after
   * 1 Jan 2027 (looked up from the future award_rate_versions row).
   */
  translatedRateAtSunset: number;
}

export interface TranslationResult {
  translatedLevel: SupportServicesLevel;
  /**
   * The applicable minimum hourly rate for the worker post-1 Jan 2027.
   * Equal to MAX(translatedRateAtSunset, hourlyRateAt31Mar2026) per J.3.
   */
  applicableRate: number;
  appliedFloor: boolean; // true if the J.3 floor was the binding constraint
  notes: string;
}

/**
 * Translate a grandfathered Dental Assistant or Pathology Collector to the
 * post-sunset Support Services classification per Schedule J + the J.3 floor.
 *
 * Throws if `legacyLevel` is not in the canonical translation table — those
 * workers fall into J.1.2 / J.2.2 ("reclassify under main A.1 definitions")
 * which is a manual review process, not algorithmic.
 */
export function translateMa000027SunsetWorker(input: TranslationInput): TranslationResult {
  let translatedLevel: SupportServicesLevel;
  if (input.legacyRole === 'DENTAL_ASSISTANT') {
    const map = DENTAL_ASSISTANT_TRANSLATION[input.legacyLevel as DentalAssistantLegacyLevel];
    if (!map) {
      throw new Error(
        `Dental Assistant legacy level "${input.legacyLevel}" is not in J.1 translation table — manual reclassification required per J.1.2.`,
      );
    }
    translatedLevel = map;
  } else {
    const map = PATHOLOGY_COLLECTOR_TRANSLATION[input.legacyLevel as PathologyCollectorLegacyLevel];
    if (!map) {
      throw new Error(
        `Pathology Collector legacy level "${input.legacyLevel}" is not in J.2 translation table — manual reclassification required per J.2.2.`,
      );
    }
    translatedLevel = map;
  }

  // J.3 no-disadvantage floor: pay the higher of translated rate vs snapshot rate
  const appliedFloor = input.hourlyRateAt31Mar2026 > input.translatedRateAtSunset;
  const applicableRate = Math.max(input.translatedRateAtSunset, input.hourlyRateAt31Mar2026);
  const notes = appliedFloor
    ? `J.3 floor applied — pre-sunset rate $${input.hourlyRateAt31Mar2026.toFixed(2)} exceeds translated rate $${input.translatedRateAtSunset.toFixed(2)}.`
    : `Translated rate $${input.translatedRateAtSunset.toFixed(2)} applies.`;

  return { translatedLevel, applicableRate, appliedFloor, notes };
}

export const SCHEDULE_J_SUNSET_DATE = '2027-01-01' as const;
export const SCHEDULE_J_SNAPSHOT_DATE = '2026-03-31' as const;
